import { ResourceHelperService } from "../resource-helper.service";
import EntitlementsResponse = InterACT.Interfaces.Entitlements.EntitlementsResponse;
import EntitlementRequestsResponse = InterACT.Interfaces.Entitlements.EntitlementRequestsResponse;

/**
 * A dataservice that provides CRUD operations on customer entries against the backend Customer service
 */
export class CustomerService {
  public customers = [];
  public totalCount = 0;
  public currentPage = 1;
  public perPageCount = 15;
  public currentPageCard = 1;
  public perPageCountCard = 15;
  public customerResource: ng.resource.IResourceClass<ng.resource.IResource<InterACT.Interfaces.Customers.CustomerResponse | InterACT.Interfaces.Customers.CustomersResponse>>;
  public pursesResource: ng.resource.IResourceClass<ng.resource.IResource<InterACT.Interfaces.Purses.PursesResponse>>;
  public entitlementsResource: ng.resource.IResourceClass<ng.resource.IResource<InterACT.Interfaces.Entitlements.EntitlementResponse | InterACT.Interfaces.Entitlements.EntitlementsResponse>>;
  public entitlementRequestsResource: ng.resource.IResourceClass<ng.resource.IResource<InterACT.Interfaces.Entitlements.EntitlementRequestResponse | InterACT.Interfaces.Entitlements.EntitlementRequestsResponse>>;
  public ticketsResource: ng.resource.IResourceClass<ng.resource.IResource<InterACT.Interfaces.Tickets.TicketsResponse>>;
  public cardsResource: ng.resource.IResourceClass<ng.resource.IResource<InterACT.Interfaces.Cards.CardsResponse>>;
  public activitiesResource: ng.resource.IResourceClass<ng.resource.IResource<any>>;
  public paymentResource: ng.resource.IResourceClass<ng.resource.IResource<any>>;
  public productSearchResource: InterACT.Interfaces.IResource<InterACT.Interfaces.ProductSearch.SearchResultsResponse>;

  constructor(private $resource, private resourceHelperService: ResourceHelperService) {
    this.pursesResource = $resource(
      'api/purses/:purseId',
      undefined,
      { edit: { method: 'PATCH' }, update: { method: 'PUT' } }, { stripTrailingSlashes: true });
    this.entitlementsResource = $resource(
      'api/entitlements/:entitlementId',
      undefined,
      {
        edit: { method: 'PATCH' },
        update: { method: 'PUT' }
      },
      { stripTrailingSlashes: true });
    this.entitlementRequestsResource = $resource(
      'api/entitlement-requests/:entitlementRequestId',
      undefined,
      {
        save: {
          method: 'POST',
          headers: {
            'Access-Control-Expose-Headers': 'Location'
          },
          transformResponse: (data, headers) => {
            const response: any = {};
            response.data = data;
            (<any>response).headers = headers();
            return response;
          }
        }
      },
      {stripTrailingSlashes: true});
    this.ticketsResource = $resource(
      'api/tickets/:ticketId',
      undefined,
      { edit: { method: 'PATCH' }, update: { method: 'PUT' } }, { stripTrailingSlashes: true });
    this.cardsResource = $resource(
      'api/cards/:cardId',
      undefined,
      { edit: { method: 'PATCH' }, update: { method: 'PUT' } }, { stripTrailingSlashes: true });
    this.activitiesResource = $resource(
      'api/activities',
      undefined,
      { get: { isArray: true }, edit: { method: 'PATCH' }, update: { method: 'PUT' } });
    this.paymentResource = $resource(
      'api/payments/:paymentId',
      undefined,
      { edit: { method: 'PATCH' }, update: { method: 'PUT' } }, { stripTrailingSlashes: true });
    this.customerResource = $resource(
      'api/customers/:customerId',
      undefined,
      {
        edit: { method: 'PATCH' },
        update: { method: 'PUT' },
        save: {
          method: 'POST',
          headers: {
            'Access-Control-Expose-Headers': 'Location'
          },
          transformResponse: (data, headers) => {
            const response: any = {};
            response.data = data;
            (<any>response).headers = headers();
            return response;
          }
        }
      }
    );
    this.productSearchResource = $resource('api/product-search/:tags');
  }

  public getListAfterUpdate() {
    if (this.customers.length === 0) {
      if (this.currentPage > 1) {
        return this.getList(this.currentPage - 1, this.perPageCount);
      }
    } else {
      return this.getList(this.currentPage, this.perPageCount);
    }
  }

  /**
   * Creates a record on the Customer backend service
   *
   * @param data The customer data object to be sent in a POST operation to the server
   *
   * @returns A http promise object
   */
  public create(data: InterACT.Interfaces.Customers.Customer) {
    return this.customerResource.save(data).$promise;
  }

  /**
   * Function to retrieve a record from the Customer backend service
   *
   * @param customerId customer Id
   *
   * @returns An array of customer records
   */
  public getCustomer(customerId: string) {
    return this.customerResource.get({ customerId: customerId }).$promise.then((resource: InterACT.Interfaces.Customers.CustomerResponse) => {
      return resource.customer;
    });
  }
  /**
   * Function to retrieve a record from the Customer backend service
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param searchText The number of items per page to be retrieved
   *
   * @returns An array of customer records
   */
  public getList(page: number, perPageCount: number, searchText?: string) {
    this.currentPage = !page ? 1 : page;
    this.perPageCount = !perPageCount ? this.perPageCount : perPageCount;
    const options: any = {
      offset: (this.currentPage - 1) * this.perPageCount,
      limit: this.perPageCount
    };
    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }
    return this.customerResource.get(options).$promise.then((resource: InterACT.Interfaces.Customers.CustomersResponse) => {
      this.customers = resource.customers;
      this.totalCount = resource.meta.totalCount;
      return resource;
    });
  }

  public update(customerId: string, data: InterACT.Interfaces.Customers.Customer) {
    return (<any>this.customerResource).update({ customerId: customerId }, data).$promise;
  }

  public remove(customerId: string) {
    return this.customerResource.delete({ customerId: customerId }).$promise.then(() => {
      return this.getListAfterUpdate();
    });
  }

  public clearList() {
    this.customers = [];
    this.totalCount = 0;
  }

  public getPurses(page: number, perPageCount: number, searchText: string) {
    this.currentPage = !page ? 1 : page;
    this.perPageCount = !perPageCount ? this.perPageCount : perPageCount;
    const options: any = {
      offset: (this.currentPage - 1) * this.perPageCount,
      limit: this.perPageCount
    };
    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }
    return this.pursesResource.get(options).$promise.then((response) => {
      return response.purses;
    });
  }

  public getTickets(page: number, perPageCount: number, searchText: string) {

    page = !page ? 1 : page;
    perPageCount = !perPageCount ? 15 : perPageCount;

    const options: any = {
      offset: (page - 1) * perPageCount,
      limit: perPageCount
    };

    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }

    return this.ticketsResource.get(options).$promise.then((response) => {
      return response;
    });
  }

  public getCards(searchText: string) {
    const options: any = {};
    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }
    return this.cardsResource.get(options).$promise.then((response) => {
      return response.cards;
    });
  }

  public getCardList(page: number, perPageCount: number, searchText?: string) {
    this.currentPageCard = !page ? 1 : page;
    this.perPageCountCard = !perPageCount ? this.perPageCountCard : perPageCount;

    const options: any = {
      offset: (this.currentPageCard - 1) * this.perPageCountCard,
      limit: this.perPageCountCard
    };

    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }
    return this.cardsResource.get(options).$promise.then((response) => {
      let res = {
        cards: response.cards,
        totalCount: response.meta.totalCount
      }
      return res;
    });
  }

  public getEntitlement(entitlementId: string) {
    return this.entitlementsResource.get({entitlementId: entitlementId}).$promise.then((resource: InterACT.Interfaces.Entitlements.EntitlementResponse) => {
      return resource.entitlement;
    });
  }

  public getEntitlements(page: number, perPageCount: number, customerID: string) {

    page = !page ? 1 : page;
    perPageCount = !perPageCount ? 15 : perPageCount;

    const options: any = {
       offset: (page - 1) * perPageCount,
       limit: perPageCount
     };

    if (angular.isDefined(customerID)) {
      options.q = customerID;
    }

    return this.entitlementsResource.get(options).$promise.then((resource : EntitlementsResponse ) => {
      return resource;
    });
  }

  public getEntitlementRequests(page: number, perPageCount: number, customerID: string) {
    page = !page ? 1 : page;
    perPageCount = !perPageCount ? 15 : perPageCount;

    const options: any = {
      offset: (page - 1) * perPageCount,
      limit: perPageCount
    };

    if (angular.isDefined(customerID)) {
      options.q = customerID;
    }

    return this.entitlementRequestsResource.get(options).$promise.then((resource: EntitlementRequestsResponse) => {
      return resource;
    });
  }

  public createEntitlementRequest(data: InterACT.Interfaces.Entitlements.EntitlementRequest) {
    return this.entitlementRequestsResource.save(data).$promise;
  }

  public updateEntitlement(entitlementId: string, data: InterACT.Interfaces.Entitlements.Entitlement) {
    return (<any>this.entitlementsResource).update({entitlementId: entitlementId}, data).$promise;
  }

  public getPayments(searchText: string, paymentId: string) {
    const options: any = {};
    if (searchText !== undefined) {
      options.q = searchText;
    }
    if (paymentId !== undefined) {
      options.paymentId = paymentId;
    }
    return this.paymentResource.get(options).$promise.then((resource) => {
      return paymentId === undefined ? resource.payments : resource.productDecrement;
    });
  }

  public getActivities(searchText: string) {
    const options: any = {};
    if (angular.isDefined(searchText)) {
      options.q = searchText;
    }
    return this.activitiesResource.get(options).$promise.then((resource) => {
      return resource;
    });
  }

  /**
   * Function to update a record from cards service
   *
   * @param cardId The number of the cardId of data to be retrieved
   * @param data The customer data object to be sent in a POST operation to the server
   * @returns A http promise object
   */
  public updateCard(cardId: string, data: InterACT.Interfaces.Cards.Card) {
    return (<any>this.cardsResource).update({ cardId: cardId }, data).$promise;
  }

  /**
   * Retrieves list of products
   *
   * @param page The number of the page of data to be retrieved
   * @param perPageCount The number of items per page to be retrieved
   * @param queryString The query to search for products
   *
   * @returns A promise of a ProductResponse
   */
  public getProducts(page: number, perPageCount: number, searchText: string, additionalFields?: Boolean) {
    const options = this.resourceHelperService.buildRequestOptions(page, perPageCount, searchText, null, null, additionalFields);
    return this.productSearchResource.get(options).$promise.then((response) => {
      return (<any>response).searchResults;
    });
  }
}
